import { FilterTrigger } from "@/components/filter/filter-trigger";
import { getComponentFromType } from "@/components/relation-display-components/view-component-renderer";
import { FormField } from "@/hooks/schema/use-schema";
import { useEntityViewComponents } from "@/hooks/use-entity-view-components";
import {
  ComponentTemplateTypes,
  EntityViewComponent,
  EntityViewComponentFieldMapping,
  EntityViewComponentFieldMappingType,
  EntityViewComponentMapping,
} from "@/services/entity-view-components.service";
import { EntitySchema } from "@/services/entity-instance.service";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { cn } from "@repo/utils/utils";
import { CheckCircle, ChevronDown, ChevronLeft, GripVertical, Plus, Trash2 } from "lucide-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";

interface RelationSelectionComponentProps {
  onSelect: (component?: EntityViewComponentMapping) => void;
  entityName: string;
  mapping?: EntityViewComponentMapping;
  /** fields for single field */
  relation_field_to_display?: string;
  targetEntitySchema: EntitySchema;
  handleRelationFieldSelect: (selectedField: FormField) => void;
  /*** end fields for single field */
}
export const EntityViewComponentSelection: React.FC<RelationSelectionComponentProps> = ({
  onSelect,
  entityName,
  mapping,
  relation_field_to_display,
  targetEntitySchema,
  handleRelationFieldSelect,
}) => {
  const { components } = useEntityViewComponents();
  const [searchValue, setSearchValue] = useState("");
  const filteredComponents = useMemo(() => {
    return components.filter((x) =>
      x.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
    );
  }, [components, searchValue]);
  const [selectedComponent, setSelectedComponent] = useState<EntityViewComponent>();
  const [showMapping, setShowMapping] = useState(false);
  const handleSelectComponent = (component: EntityViewComponent) => {
    setSelectedComponent(component);
    setShowMapping(true);
  };
  const handleBack = () => {
    setShowMapping(false);
  };

  return (
    <Dialog>
      <DialogTrigger>
        {mapping?.component ?? relation_field_to_display ?? "select a component"}
      </DialogTrigger>
      <DialogContent className="max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select View Component</DialogTitle>
          <DialogDescription>
            Choose how to display <span className="font-bold">{entityName}</span> in your view.
          </DialogDescription>
        </DialogHeader>
        {!showMapping || !selectedComponent ? (
          <div>
            <Label>Search</Label>
            <Input
              placeholder="Search for components"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <div className="grid gap-2 overflow-y-auto pt-3">
              <Card
                key={"single_field"}
                onClick={() =>
                  handleSelectComponent({
                    type: "SINGLE_FIELD" as unknown as ComponentTemplateTypes,
                    default_opts: {},
                    fields: [],
                    name: "Single field",
                  })
                }
                className="cursor-pointer hover:bg-slate-50"
              >
                <CardHeader>
                  <CardTitle>{"Single field"}</CardTitle>
                  <CardDescription>{"A single field displayed"}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg bg-slate-100 px-4 py-2">
                    <p>Name</p>
                  </div>
                </CardContent>
              </Card>
              {filteredComponents?.map((c, idx) => {
                const Component = getComponentFromType(c.type);
                if (!Component) return null;
                return (
                  <Card
                    key={idx}
                    onClick={() => handleSelectComponent(c)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    {selectedComponent?.type === c.type && (
                      <div className="relative">
                        <div className="absolute right-2 top-2">
                          <CheckCircle size={"1rem"} />
                        </div>
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle>{c.name}</CardTitle>
                      <CardDescription>{c.description}</CardDescription>
                    </CardHeader>

                    <CardContent>
                      <div className="rounded-lg bg-slate-100">
                        <Component config={c.default_opts} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {(selectedComponent.type as unknown) == "SINGLE_FIELD" ? (
              <div className="flex flex-col gap-4">
                <div>
                  <span className="font-bold">Field: </span>
                  <FilterTrigger
                    entitySchema={targetEntitySchema as unknown as EntitySchema}
                    onSelect={handleRelationFieldSelect}
                    fieldsToHide={["created_at", "updated_at", "integer_id"]}
                    amountLevels={1}
                    hideListRelations={true}
                  >
                    <div className="inline-flex h-8 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-2 font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
                      {relation_field_to_display || "Select field"}
                      <ChevronDown className="ml-1 h-3 w-3" />
                    </div>
                  </FilterTrigger>
                </div>

                <DialogFooter onClick={handleBack}>
                  <Button variant={"outline"}>
                    <ChevronLeft />
                    Back
                  </Button>
                  <DialogClose asChild>
                    <Button className="grow" onClick={() => onSelect(undefined)}>
                      Ok
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </div>
            ) : (
              <ComponentFieldMapping
                targetEntitySchema={targetEntitySchema}
                selectedComponent={selectedComponent}
                key={selectedComponent.type}
                mappingConfig={mapping?.config as string}
                setMappingConfig={(config) =>
                  onSelect({
                    ...mapping,
                    component: selectedComponent.type,
                    config: config,
                  })
                }
                onBack={handleBack}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface ComponentFieldMappingProps {
  selectedComponent: EntityViewComponent;
  mappingConfig?: string;
  setMappingConfig: (config: string) => void;
  targetEntitySchema: EntitySchema;
  onBack: () => void;
}
const ComponentFieldMapping: React.FC<ComponentFieldMappingProps> = ({
  selectedComponent,
  mappingConfig,
  targetEntitySchema,
  setMappingConfig,
  onBack,
}) => {
  const [config, setConfig] = useState(
    !!mappingConfig ? (JSON.parse(mappingConfig) as Array<EntityViewComponentFieldMapping>) : [],
  );
  const Render = getComponentFromType(selectedComponent.type)!;

  const configOpt = useMemo(() => {
    if (config.length === 0) return selectedComponent.default_opts;
    const res: { [key: string]: string } = {};
    config.forEach((c) => {
      res[c.componentField] = c.entityFields
        .filter((x) => !!x)
        .map((x) => "${" + x + "}")
        .join(c.type === "combine-spaces" ? " " : " || ");
    });
    return res;
  }, [selectedComponent, config]);

  return (
    <div className="gap flex flex-col gap-4">
      <div className="rounded-lg bg-slate-100">
        <Render config={configOpt} />
      </div>
      <div></div>
      <MappingInfo componentName={selectedComponent.name} />

      {selectedComponent.fields.map((c, idx) => {
        const mapping = config.find((x) => x.componentField === c.field_name) ?? {
          type: "combine-spaces",
          entityFields: [""],
          componentField: c.field_name,
        };
        return (
          <FieldMappings
            key={idx}
            fieldName={c.field_name}
            required={c.required}
            targetEntitySchema={targetEntitySchema}
            mapping={mapping}
            onMappingChange={(newMapping) => {
              const updatedMapping =
                typeof newMapping === "function" ? newMapping(mapping) : newMapping;
              setConfig((prev) => {
                const newConf = prev.filter(
                  (x) => x.componentField !== updatedMapping.componentField,
                );
                return [...newConf, updatedMapping];
              });
            }}
          />
        );
      })}

      <div className="border-b"></div>
      <DialogFooter onClick={onBack}>
        <Button variant={"outline"}>
          <ChevronLeft />
          Back
        </Button>
        <DialogClose asChild>
          <Button className="grow" onClick={() => setMappingConfig(JSON.stringify(config))}>
            Save
          </Button>
        </DialogClose>
      </DialogFooter>
    </div>
  );
};

interface MappingInfoProps {
  componentName: string;
}
const MappingInfo: React.FC<MappingInfoProps> = ({ componentName }) => {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-blue-500"></div>
        <span className="font-medium text-blue-900">{componentName} Component</span>
      </div>
      <p className="text-sm text-blue-700">
        Map your data fields to the component&apos;s expected fields. Choose to combine multiple
        fields or use them as fallbacks.
      </p>
    </div>
  );
};

interface FieldMappingsProps {
  mapping: EntityViewComponentFieldMapping;
  onMappingChange: Dispatch<SetStateAction<EntityViewComponentFieldMapping>>;
  targetEntitySchema: EntitySchema;
  fieldName: string;
  required?: boolean;
}
const FieldMappings: React.FC<FieldMappingsProps> = ({
  mapping,
  required,
  fieldName,
  onMappingChange,
  targetEntitySchema,
}) => {
  const handleFieldMappingDragEnd = (result: DropResult<string>) => {
    const items = [...mapping.entityFields];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination?.index ?? 0, 0, reorderedItem);

    onMappingChange((prev) => ({
      ...prev,
      entityFields: items,
    }));
  };

  return (
    <Card>
      <CardHeader className="hidden"></CardHeader>
      <CardContent>
        <div className="flex w-full items-center justify-between gap-2 py-2">
          <p className="font-medium">
            {fieldName} {required && <span className="text-red-600">*</span>}
          </p>
          {mapping.entityFields.length > 1 && (
            <MultipleFieldsModeSelect
              mappingMode={mapping.type}
              onMappingChange={onMappingChange}
            />
          )}
        </div>
        <DragDropContext onDragEnd={(res) => handleFieldMappingDragEnd(res)}>
          <Droppable droppableId="field">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {mapping.entityFields.map((field, idx) => (
                  <Draggable key={`field-${idx}`} draggableId={`field-${idx}`} index={idx}>
                    {(provided, snapshot) => {
                      const child = (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`group flex w-full items-center justify-between gap-2 ${snapshot.isDragging ? "opacity-50" : ""}`}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab rounded p-1 hover:bg-gray-100 active:cursor-grabbing"
                          >
                            <GripVertical className="h-4 w-4 text-gray-400" />
                          </div>

                          <FilterTrigger
                            entitySchema={targetEntitySchema}
                            onSelect={(field) => {
                              const entityFields = [...mapping.entityFields];
                              entityFields[idx] = field.name;
                              onMappingChange((prev) => ({
                                ...prev,
                                entityFields,
                              }));
                            }}
                            amountLevels={1}
                            asChild
                          >
                            <div className="inline-flex h-8 grow items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-background px-2 text-left font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
                              {field || "Select field"}
                              <ChevronDown className="ml-1 h-3 w-3" />
                            </div>
                          </FilterTrigger>
                          <button
                            className="text-neutral-600 opacity-10 group-hover:opacity-100"
                            onClick={() => {
                              const entityFields = mapping.entityFields.filter((_, i) => idx != i);
                              onMappingChange((prev) => ({
                                ...prev,
                                entityFields,
                              }));
                            }}
                          >
                            <Trash2 size={"1rem"} />
                          </button>
                        </div>
                      );
                      if (snapshot.isDragging) {
                        return createPortal(child, document.body);
                      }
                      return child;
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <Button
          onClick={() => {
            onMappingChange((prev) => ({
              ...prev,
              entityFields: [...prev.entityFields, ""],
            }));
          }}
          variant={"outline"}
          disabled={mapping.entityFields.some((x) => !x)}
          className={cn("mt-4 flex w-full items-center gap-2", {
            "text-muted-foreground": mapping.entityFields.some((x) => !x),
          })}
        >
          <Plus size={"1rem"} />
          Add new field
        </Button>
      </CardContent>
    </Card>
  );
};

interface MultipleFieldsModeSelectProps {
  mappingMode: EntityViewComponentFieldMappingType;
  onMappingChange: Dispatch<SetStateAction<EntityViewComponentFieldMapping>>;
}
const MultipleFieldsModeSelect: React.FC<MultipleFieldsModeSelectProps> = ({
  onMappingChange,
  mappingMode,
}) => {
  return (
    <Select
      value={mappingMode}
      onValueChange={(value) =>
        onMappingChange((prev) => ({
          ...prev,
          type: value as EntityViewComponentFieldMappingType,
        }))
      }
    >
      <SelectTrigger className="max-w-[200px]">
        <SelectValue></SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="combine-spaces">
          <p>Combine</p>
          <span className="text-muted-foreground">Join all fields together</span>
        </SelectItem>
        <SelectItem value="fallback">
          <p>Fallback</p>
          <span className="text-muted-foreground">Use first available field</span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};
